import { Injectable, OnDestroy, inject } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class SignalRService implements OnDestroy {
  private authService = inject(AuthService);

  private hubConnection: signalR.HubConnection | null = null;
  private readonly hubUrl = `${environment.apiUrl}/hubs/notifications`;

  /**
   * The in-flight (or completed) start of the current connection. Non-null means the
   * current connection is connected, still negotiating, or auto-reconnecting — all
   * states in which a caller must join it rather than build another. Memoized because
   * the hub has concurrent callers during app load (the header's notification wiring
   * and the dashboard's activity feed): before this, the second call landed while the
   * first was still Connecting, rebuilt the connection, orphaned the first socket, and
   * left handlers registered on whichever object the field happened to hold. Cleared
   * when a start fails or the connection closes for good, so the next caller rebuilds.
   */
  private startPromise: Promise<void> | null = null;

  private readonly _messageReceived$ = new Subject<{ method: string; args: unknown[] }>();
  readonly messageReceived$ = this._messageReceived$.asObservable();

  startConnection(): Promise<void> {
    if (this.startPromise) {
      return this.startPromise;
    }

    const connection = this.buildHubConnection();

    connection.onreconnected(() => {
      console.log('SignalR reconnected.');
    });

    connection.onclose((error) => {
      // Automatic reconnects are exhausted (or the connection was stopped): it will
      // never carry messages again, so stop satisfying startConnection with it. The
      // identity check keeps a stale connection's late close event from clobbering
      // the memo of a newer connection.
      if (this.hubConnection === connection) {
        this.startPromise = null;
      }
      if (error) {
        console.error('SignalR connection closed with error:', error);
      }
    });

    this.hubConnection = connection;
    this.startPromise = connection.start().catch((error) => {
      if (this.hubConnection === connection) {
        this.startPromise = null;
      }
      throw error;
    });
    return this.startPromise;
  }

  /**
   * Seam for tests — a real HubConnection cannot be built or started without a live
   * hub, so specs override this to hand back a controllable fake.
   */
  protected buildHubConnection(): signalR.HubConnection {
    return new signalR.HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        accessTokenFactory: () => this.authService.getAccessToken().then(t => t ?? ''),
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();
  }

  on<T>(method: string, callback: (data: T) => void): void {
    this.hubConnection?.on(method, callback);
  }

  off(method: string): void {
    this.hubConnection?.off(method);
  }

  async stopConnection(): Promise<void> {
    this.startPromise = null;
    await this.hubConnection?.stop();
    this.hubConnection = null;
  }

  ngOnDestroy(): void {
    this.stopConnection();
  }
}
